import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";

import {fromEvent} from "rxjs";
import {TranslateService} from "@ngx-translate/core";

import {SetPasswordRequest} from "@commons/models/remote.request/SetPasswordRequest";
import {CustomValidators} from "@commons/validators/validators";
import {WebAccessService} from '@commons/services/web.access/web-access.service';

import {BaseComponent} from "@shared/components/base-component";
import {UserHttpService} from "@shared/services-http/user-http.service";

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent extends BaseComponent implements OnInit {

  @Output() passwordChanged = new EventEmitter<boolean>();

  private readonly oldPasswordIncorrectErrorCode = 9;
  private readonly requiredErrorKey = 'required';
  private readonly weekPasswordErrorKey = 'weekPassword';
  private readonly passwordsNotEqualErrorKey = 'passwordsNotEqual';
  private readonly oldPasswordIncorrectErrorKey = 'oldPasswordIncorrect';

  fieldIsRequiredErrorMessage = '';
  passwordRequirementsErrorMessage = '';
  passwordsNotEqualErrorMessage = '';
  oldPasswordErrorMessage = '';

  form!: FormGroup;
  isLoading = false;
  oldPasswordError = false;

  oldPasswordCtrl = new FormControl('', Validators.compose([Validators.required, this.oldPasswordValidator()]));
  newPasswordCtrl = new FormControl('', Validators.compose([Validators.required, CustomValidators.passwordStrength, this.passwordsEqualValidator()]));
  newPasswordRepeatCtrl = new FormControl('', Validators.compose([Validators.required, CustomValidators.passwordStrength, this.passwordsEqualValidator()]));

  constructor(
    private activatedRoute: ActivatedRoute,
    private webAccessService: WebAccessService,
    private formBuilder: FormBuilder,
    private userHttpService: UserHttpService,
    private translateService: TranslateService
  ) {
    super();
    this.setTranslations();
  }

  private setTranslations() {
    this.translateService.get('errors.field-is-required').subscribe(t => this.fieldIsRequiredErrorMessage = t);
    this.translateService.get('password-change-shared.password-requirements-error').subscribe(t => this.passwordRequirementsErrorMessage = t);
    this.translateService.get('password-change-shared.passwords-not-equal-error').subscribe(t => this.passwordsNotEqualErrorMessage = t);
    this.translateService.get('change-password.old-password').subscribe(t => this.oldPasswordErrorMessage = t);
  }

  ngOnInit(): void {
    this.createForm();
    this.subscribeToFormValueChanges();
    this.subscribeToKeyboardEnter();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  getErrorMessageElementId(controlName: string, control: AbstractControl): string {
    let errorName = '';

    if (control.hasError(this.requiredErrorKey))
      errorName = this.requiredErrorKey;

    else if (control.hasError(this.weekPasswordErrorKey))
      errorName = this.weekPasswordErrorKey;

    else if (control.hasError(this.passwordsNotEqualErrorKey))
      errorName = this.passwordsNotEqualErrorKey;

    else if (control.hasError(this.oldPasswordIncorrectErrorKey))
      errorName = this.oldPasswordIncorrectErrorKey;

    return `${controlName}-error-${errorName}`;
  }

  getErrorMessage(control: AbstractControl): string {
    if (control.hasError(this.requiredErrorKey))
      return this.fieldIsRequiredErrorMessage;

    if (control.hasError(this.weekPasswordErrorKey))
      return this.passwordRequirementsErrorMessage;

    if (control.hasError(this.passwordsNotEqualErrorKey))
      return this.passwordsNotEqualErrorMessage;

    if (control.hasError(this.oldPasswordIncorrectErrorKey))
      return this.oldPasswordErrorMessage;

    return "";
  }

  onSave() {
    this.form.markAllAsTouched();
    this.oldPasswordError = false;
    this.oldPasswordCtrl.updateValueAndValidity();

    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;
    this.userHttpService.setUserPassword(this.getSetPasswordRequest())
      .subscribe({
        next: (result) => {
          if (result.ErrorCode === this.oldPasswordIncorrectErrorCode)
            this.afterChangePasswordError();
          else
            this.afterChangePassword();
        },
      });
  }

  private getSetPasswordRequest(): SetPasswordRequest {
    const model = {
      OldPassword: this.oldPasswordCtrl.value,
      NewPassword: this.newPasswordCtrl.value,
    } as SetPasswordRequest;
    return model;
  }

  private afterChangePasswordError() {
    this.isLoading = false;
    this.oldPasswordError = true;
    this.oldPasswordCtrl.setErrors({oldPasswordIncorrect: true});
    this.oldPasswordCtrl.updateValueAndValidity({emitEvent: false});
  }

  private afterChangePassword() {
    this.isLoading = false;
    this.passwordChanged.emit(true);
  }

  private passwordsEqualValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {

      const arePasswordsNotEqual =
        this.form != null &&
        this.form.controls != null &&
        this.newPasswordCtrl.value !== this.newPasswordRepeatCtrl.value

      return arePasswordsNotEqual ? {passwordsNotEqual: true} : null;
    };
  }

  private oldPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.oldPasswordError ? {oldPasswordIncorrect: true} : null;
    };
  }

  private subscribeToFormValueChanges() {
    this.addToUnsubscribe(this.form.valueChanges.subscribe(newValue => {
      this.arePasswordsNotEqual(newValue);
    }));
  }

  private arePasswordsNotEqual(newValue: string) {
    const notEqual = this.newPasswordCtrl.value !== this.newPasswordRepeatCtrl.value;

    this.newPasswordCtrl.setErrors({passwordsNotEqual: notEqual ?? null});
    this.newPasswordCtrl.updateValueAndValidity({emitEvent: false});

    this.newPasswordRepeatCtrl.setErrors({passwordsNotEqual: notEqual ?? null});
    this.newPasswordRepeatCtrl.updateValueAndValidity({emitEvent: false});
  }

  private createForm() {
    this.form = this.formBuilder.group({
      oldPassword: this.oldPasswordCtrl,
      newPassword: this.newPasswordCtrl,
      newPasswordRepeat: this.newPasswordRepeatCtrl,
    });
  }

  private subscribeToKeyboardEnter() {
    this.addToUnsubscribe(fromEvent(document, 'keyup').subscribe(e => {
      const event = e as KeyboardEvent;
      if (event.key === 'Enter') {
        this.onSave();
      }
    }));
  }

}
